import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Box, Button, Divider, Typography, useTheme } from '@mui/material';

import Dialog from 'components/dialog';
import List from 'components/list/list';
import ListRow from 'components/list/list-row';
import ListCell from 'components/list/list-cell';
import NewListRow from 'components/new-list-row';
import LoadingSpinner from 'components/loading-spinner';

import { useOrdersState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/orders-context';
import { OfdFeedProgram } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/feed-programs.type';

type Props = {};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders';
const DIALOG_ID = `${PAGE_NAME_ID}.modals.viewFeedProgramsDialog`;

const columns = [
  { id: 'fishGroup', label: <FormattedMessage id={`${DIALOG_ID}.columns.fishGroup`} /> },
  { id: 'latestWeight', label: <FormattedMessage id={`${DIALOG_ID}.columns.latestWeight`} /> },
  { id: 'feedProgram', label: <FormattedMessage id={`${DIALOG_ID}.columns.feedProgram`} /> },
  { id: 'harvestTarget', label: <FormattedMessage id={`${DIALOG_ID}.columns.harvestTarget`} /> },
];

const feedStrategyLineOptions = [
  { id: 'transferWeights', label: <FormattedMessage id={`${DIALOG_ID}.options.transferWeights`} /> },
  { id: 'feeds', label: <FormattedMessage id={`${DIALOG_ID}.options.feeds`} /> },
];

const renderHarvestTarget = (item: OfdFeedProgram) => {
  if (item.harvestByWeight) {
    return item.harvestMeanWeight > 0 ? (
      <FormattedNumber value={item.harvestMeanWeight} maximumFractionDigits={0} style="unit" unit="gram" />
    ) : (
      '-'
    );
  }
  return item.harvestDate ? <FormattedDate value={item.harvestDate} day="2-digit" month="short" year="numeric" /> : '-';
};

const ViewFeedProgramsDialog: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();

  const { ofdFeedProgramsQuery: { data: feedPrograms, isFetching } } = useOrdersState();

  return (
    <Dialog
      title="Feed programs"
      maxWidth="md"
      button={
        <Button size="small" variant="contained">
          {intl.formatMessage({ id: `${DIALOG_ID}.viewFeedProgramsBtn` })}
        </Button>
      }>
      <Box display="flex" flexDirection="column" gap={4}>
        <Divider />
        {isFetching ? (
          <LoadingSpinner />
        ) : (
          <List
            dark
            options={columns}
            sx={{
              ['.MuiTableCell-body']: {
                backgroundColor: theme.palette.lightBlue.o25,
              },
            }}>
            {(feedPrograms ?? []).map((item) => (
              <NewListRow
                key={item.fishGroupId}
                colSpan={columns.length + 1}
                body={
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Divider sx={{ borderColor: theme.palette.darkBlue.o25 }} />
                    {item.feedStrategyLines.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        <FormattedMessage id={`${DIALOG_ID}.noFeedStrategyAssigned`} />
                      </Typography>
                    ) : (
                      <List options={feedStrategyLineOptions}>
                        {item.feedStrategyLines.map((line, index) => (
                          <ListRow key={index}>
                            <ListCell>{line.fromWeight} g:</ListCell>
                            <ListCell>{line.itemName}</ListCell>
                          </ListRow>
                        ))}
                      </List>
                    )}
                  </Box>
                }>
                <ListCell>{item.fishGroupName}</ListCell>
                <ListCell>
                  {item.currentMeanWeight ? (
                    <FormattedNumber value={item.currentMeanWeight} maximumFractionDigits={0} style="unit" unit="gram" />
                  ) : (
                    '-'
                  )}
                </ListCell>
                <ListCell>{item.feedStrategyName ?? '-'}</ListCell>
                <ListCell>{renderHarvestTarget(item)}</ListCell>
              </NewListRow>
            ))}
          </List>
        )}
      </Box>
    </Dialog>
  );
};

export default ViewFeedProgramsDialog;
